import { isCanUseCallSettings } from 'util/getCallType';
import { normalizeJoinPolicy, VCS_JOIN_POLICY } from 'consts';
import { isCallInitiator } from 'features/AccessRequests/utils/canBypassWaitingRoom';
import { callPolicyDraftFromMessenger } from 'features/CallSettings/utils/joinPolicyFromMessenger';
import { callPolicyStore } from 'state/store';
import { CallJoinPolicy } from 'features/AccessRequests/api/accessRequestsApi';
import { selectLocalParticipant, selectRoomId, selectRoomMetadata } from 'state/selectors';
import { isLocalUserModerator } from 'useCase/moderation/status/moderators';
import { clearInRoomFlag, clearLivekitTokens } from '../waitingRoom';
import { setCallLsPolicy } from '../callLsData';

/** Актуальная политика: metadata комнаты, иначе draft (prejoin / CALL_DATA). */
export const resolveEffectiveJoinPolicy = (): CallJoinPolicy => {
    if (!isCanUseCallSettings()) {
        return VCS_JOIN_POLICY.ALL;
    }

    const fromMetadata = selectRoomMetadata()?.joinApprovalData?.joinApprovalPolicy;
    if (fromMetadata != null) {
        return normalizeJoinPolicy(fromMetadata);
    }

    return callPolicyStore.joinPolicy;
};

/** Актуальный флаг одобряют только модераторы: metadata комнаты, иначе draft. */
export const resolveEffectiveModeratorsApprovalOnly = (): boolean => {
    if (!isCanUseCallSettings()) {
        return false;
    }

    const fromMetadata = selectRoomMetadata()?.joinApprovalData?.onlyModeratorsCanApproveWaitingUsers;
    if (fromMetadata != null) {
        return Boolean(fromMetadata);
    }

    return callPolicyStore.moderatorsApprovalOnly;
};

/** При выходе из комнаты под «все с запросом» — сбросить кешированный JWT, чтобы следующий вход прошёл через waiting room. */
export const shouldClearCachedAdmissionOnLeave = (roomId: string): boolean => {
    if (!isCanUseCallSettings()) return false;
    if (isCallInitiator(roomId) || isLocalUserModerator()) return false;
    return resolveEffectiveJoinPolicy() === VCS_JOIN_POLICY.ALL_WITH_APPROVAL;
};

/** Применить join-approval-policy из CALL_DATA / metadata в локальный draft. */
export const syncCallPolicyDraftFromServer = (payload: {
    joinApprovalPolicy?: string | null;
    onlyModeratorsCanApproveWaitingUsers?: boolean | null;
}): void => {
    if (!isCanUseCallSettings() || payload.joinApprovalPolicy == null) return;
    const draft = callPolicyDraftFromMessenger(payload);
    callPolicyStore.set(draft);
    // Дублируем в localStorage: sessionStorage.clear() в webEntry сбрасывает всё при F5.
    // Данные привязаны к roomName — устаревшие данные других комнат будут очищены при чтении.
    const roomName = selectRoomId();
    if (roomName) {
        setCallLsPolicy(roomName, draft);
    }
};

/**
 * Стала ли политика строже (open → approval, или GUESTS → ALL)?
 * Чистая функция — без side effects, тестируема изолированно.
 */
export const shouldInvalidateCachedAdmission = (
    previous: string | null | undefined,
    next: string | null | undefined,
    roomId?: string,
): boolean => {
    if (normalizeJoinPolicy(next) === VCS_JOIN_POLICY.ALL) return false;

    const stricter =
        normalizeJoinPolicy(previous) === VCS_JOIN_POLICY.ALL ||
        (previous === VCS_JOIN_POLICY.GUESTS_WITH_APPROVAL && next === VCS_JOIN_POLICY.ALL_WITH_APPROVAL);
    if (!stricter) return false;

    const resolvedRoomId = roomId ?? selectRoomId() ?? undefined;
    if (resolvedRoomId && isCallInitiator(resolvedRoomId)) return false;
    if (isLocalUserModerator()) return false;

    return selectLocalParticipant() == null;
};

/**
 * Политика стала строже — сбросить кешированный bypass (JWT / IN_ROOM),
 * чтобы следующий вход шёл через waiting room API.
 */
export const handleStricterJoinApprovalPolicy = (
    previous: string | null | undefined,
    next: string | null | undefined,
    roomId?: string,
): void => {
    if (shouldInvalidateCachedAdmission(previous, next, roomId)) {
        clearLivekitTokens();
        clearInRoomFlag();
    }
};
